import { collection, getDocs, query, where, doc, getDoc, setDoc, writeBatch, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase/config";

// Shape of the exported backup file
interface BackupData {
  metadata: {
    exportDate: string;
    userId: string;
    version: string;
  };
  expenses: Record<string, unknown>[];
  income: Record<string, unknown>[];
  investments: Record<string, unknown>[];
  bankAccounts: Record<string, unknown>[];
  payerColors: Record<string, string>;
  categoryIcons: Record<string, string>;
}

/**
 * Export all user data from Firestore to a JSON file and trigger a download.
 */
export const exportAllData = async (userId: string): Promise<void> => {
  try {
    // 1. Fetch expenses
    const expensesQuery = query(collection(db, "expenses"), where("userId", "==", userId));
    const expensesSnapshot = await getDocs(expensesQuery);
    const expenses = expensesSnapshot.docs.map(d => {
      const data = d.data();
      return {
        firestoreId: d.id,
        ...data,
        // Convert Timestamp to ISO string for portability
        date: data.date instanceof Timestamp ? data.date.toDate().toISOString() : data.date,
      };
    });

    // 2. Fetch income
    const incomeQuery = query(collection(db, "income"), where("userId", "==", userId));
    const incomeSnapshot = await getDocs(incomeQuery);
    const income = incomeSnapshot.docs.map(d => {
      const data = d.data();
      return {
        firestoreId: d.id,
        ...data,
        date: data.date instanceof Timestamp ? data.date.toDate().toISOString() : data.date,
      };
    });

    // 3. Fetch investments
    const investmentsQuery = query(collection(db, "investments"), where("userId", "==", userId));
    const investmentsSnapshot = await getDocs(investmentsQuery);
    const investments = investmentsSnapshot.docs.map(d => {
      const data = d.data();
      return {
        firestoreId: d.id,
        ...data,
        purchaseDate: data.purchaseDate instanceof Timestamp
          ? data.purchaseDate.toDate().toISOString()
          : data.purchaseDate,
      };
    });

    // 4. Fetch bank accounts
    const bankAccountsQuery = query(collection(db, "bankAccounts"), where("userId", "==", userId));
    const bankAccountsSnapshot = await getDocs(bankAccountsQuery);
    const bankAccounts = bankAccountsSnapshot.docs.map(d => ({
      firestoreId: d.id,
      ...d.data(),
    }));

    // 5. Fetch payerColors (shared doc)
    const payerColorsDoc = await getDoc(doc(db, "payerColors", "colors"));
    const payerColors = payerColorsDoc.exists() ? (payerColorsDoc.data() as Record<string, string>) : {};

    // 6. Fetch categoryIcons (shared doc)
    const categoryIconsDoc = await getDoc(doc(db, "categoryIcons", "icons"));
    const categoryIcons = categoryIconsDoc.exists() ? (categoryIconsDoc.data() as Record<string, string>) : {};

    // Build backup object
    const backup: BackupData = {
      metadata: {
        exportDate: new Date().toISOString(),
        userId,
        version: "1.0",
      },
      expenses,
      income,
      investments,
      bankAccounts,
      payerColors,
      categoryIcons,
    };

    // Trigger download
    const jsonString = JSON.stringify(backup, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dateStr = new Date().toISOString().split("T")[0];
    a.download = `backup_expense_tracker_${dateStr}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting data:", error);
    throw new Error(`Failed to export data: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
};

/**
 * Import data from a JSON backup file into Firestore.
 * This deletes existing user data and replaces it with the backup.
 * Returns a summary of what was imported.
 */
export const importAllData = async (
  userId: string,
  file: File
): Promise<{ expenses: number; income: number; investments: number; bankAccounts: number }> => {
  try {
    const text = await file.text();
    const backup: BackupData = JSON.parse(text);

    // Validate the backup structure
    if (!backup.metadata || !backup.metadata.version) {
      throw new Error("Invalid backup file: missing metadata");
    }

    // --- 1. Delete existing user data ---
    const collectionsToDelete = ["expenses", "income", "investments", "bankAccounts"];
    for (const collName of collectionsToDelete) {
      const q = query(collection(db, collName), where("userId", "==", userId));
      const snapshot = await getDocs(q);
      // Firestore batch limit is 500 operations
      const batches: ReturnType<typeof writeBatch>[] = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      snapshot.docs.forEach((d) => {
        currentBatch.delete(d.ref);
        opCount++;
        if (opCount % 499 === 0) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
        }
      });
      batches.push(currentBatch);
      for (const batch of batches) {
        await batch.commit();
      }
    }

    // --- 2. Import expenses ---
    if (backup.expenses?.length) {
      const batches: ReturnType<typeof writeBatch>[] = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      for (const expense of backup.expenses) {
        const { firestoreId, ...data } = expense as Record<string, unknown>;
        const docRef = firestoreId
          ? doc(db, "expenses", firestoreId as string)
          : doc(collection(db, "expenses"));
        currentBatch.set(docRef, {
          ...data,
          userId,
          date: data.date ? Timestamp.fromDate(new Date(data.date as string)) : Timestamp.now(),
        });
        opCount++;
        if (opCount % 499 === 0) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
        }
      }
      batches.push(currentBatch);
      for (const batch of batches) {
        await batch.commit();
      }
    }

    // --- 3. Import income ---
    if (backup.income?.length) {
      const batches: ReturnType<typeof writeBatch>[] = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      for (const inc of backup.income) {
        const { firestoreId, ...data } = inc as Record<string, unknown>;
        const docRef = firestoreId
          ? doc(db, "income", firestoreId as string)
          : doc(collection(db, "income"));
        currentBatch.set(docRef, {
          ...data,
          userId,
          date: data.date ? Timestamp.fromDate(new Date(data.date as string)) : Timestamp.now(),
        });
        opCount++;
        if (opCount % 499 === 0) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
        }
      }
      batches.push(currentBatch);
      for (const batch of batches) {
        await batch.commit();
      }
    }

    // --- 4. Import investments ---
    if (backup.investments?.length) {
      const batches: ReturnType<typeof writeBatch>[] = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      for (const inv of backup.investments) {
        const { firestoreId, ...data } = inv as Record<string, unknown>;
        const docRef = firestoreId
          ? doc(db, "investments", firestoreId as string)
          : doc(collection(db, "investments"));
        currentBatch.set(docRef, {
          ...data,
          userId,
          purchaseDate: data.purchaseDate
            ? Timestamp.fromDate(new Date(data.purchaseDate as string))
            : null,
        });
        opCount++;
        if (opCount % 499 === 0) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
        }
      }
      batches.push(currentBatch);
      for (const batch of batches) {
        await batch.commit();
      }
    }

    // --- 5. Import bank accounts ---
    if (backup.bankAccounts?.length) {
      const batches: ReturnType<typeof writeBatch>[] = [];
      let currentBatch = writeBatch(db);
      let opCount = 0;
      for (const account of backup.bankAccounts) {
        const { firestoreId, ...data } = account as Record<string, unknown>;
        const docRef = firestoreId
          ? doc(db, "bankAccounts", firestoreId as string)
          : doc(collection(db, "bankAccounts"));
        currentBatch.set(docRef, {
          ...data,
          userId,
        });
        opCount++;
        if (opCount % 499 === 0) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
        }
      }
      batches.push(currentBatch);
      for (const batch of batches) {
        await batch.commit();
      }
    }

    // --- 6. Import payerColors ---
    if (backup.payerColors && Object.keys(backup.payerColors).length > 0) {
      await setDoc(doc(db, "payerColors", "colors"), backup.payerColors);
    }

    // --- 7. Import categoryIcons ---
    if (backup.categoryIcons && Object.keys(backup.categoryIcons).length > 0) {
      await setDoc(doc(db, "categoryIcons", "icons"), backup.categoryIcons);
    }

    return {
      expenses: backup.expenses?.length || 0,
      income: backup.income?.length || 0,
      investments: backup.investments?.length || 0,
      bankAccounts: backup.bankAccounts?.length || 0,
    };
  } catch (error) {
    console.error("Error importing data:", error);
    throw new Error(`Failed to import data: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
};
